import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { Checkbox } from '@mui/material';
import { t } from 'i18next';

import reducer from './reducer';
import { expandScholarshipApplications } from '../../../../utils/expandScholarshipApplications';

const initialState = {
    page: 0,
    rowsPerPage: 25,
    filterString: '',
    sort: 'StudentLastName Asc',
    recOffset: 0,
    searchValue: '',
    searchKeys: ['StudentLastName', 'StudentFirstName', 'Email'],
    searchString: '',
    dateString: '',
    filterObjectString: '',
    sortKey: 'StudentLastName',
    sortDir: 'Asc',
    isFetching: true,
    filters: {},
    totalRecordCount: 0,
    totalUnfilteredCount: 0,
    currentCount: 0,
    headers: {},
    list: [],
    hasMore: true,
    isInitialized: false,
};

const useNotificationDashboard = () => {
    const [requestObj, dispatch] = React.useReducer(reducer, initialState);
    const [selected, setSelected] = React.useState(new Set());

    const retrieveList = async (filterString, sort, recOffset) => {
        let requestBody = {
            FilterString: filterString !== undefined ? filterString : requestObj.filterString,
            Sort: sort !== undefined ? sort : requestObj.sort,
            RecOffset: recOffset !== undefined ? recOffset : requestObj.recOffset,
        };

        dispatch({ type: 'fetching' });

        try {
            let [response, filters] = await Promise.all([
                API.post('ShipyardAPI', '/applications/scholarship', {
                    body: {
                        pFilterString: requestBody.FilterString,
                        pSort: requestBody.Sort,
                        pRecOffset: requestBody.RecOffset,
                    },
                }),
                API.get('ShipyardAPI', '/applications/filters'),
            ]);

            const expandedData = expandScholarshipApplications(response);
            let _list = requestBody.RecOffset === 0 ? expandedData : [...requestObj.list, ...expandedData];
            _list = _list.map((item) => {
                return {
                    ...item,
                    StatusID: item.Status,
                    Selected: (e) => {
                        if (e?.target?.checked) {
                            setSelected((prev) => {
                                prev.add(`${item.ApplicationInstanceId}|${item.NotificationEmailLocation}`);
                                return new Set(prev);
                            });
                        } else {
                            setSelected((prev) => {
                                prev.delete(`${item.ApplicationInstanceId}|${item.NotificationEmailLocation}`);
                                return new Set(prev);
                            });
                        }
                    },
                };
            });

            const _headers = {
                Selected: (
                    <Checkbox
                        size="small"
                        onChange={(e) => {
                            if (e.target.checked)
                                setSelected(
                                    new Set(_list?.map((entry) => `${entry.ApplicationInstanceId}|${entry.NotificationEmailLocation}`))
                                );
                            else {
                                setSelected(new Set());
                            }
                        }}
                    />
                ),
                ApplicationInstanceId: 'Application ID',
                StudentLastName: 'Last Name',
                StudentFirstName: 'First Name',
                NotificationEmailLocation: 'Email',
                StatusID: 'Status',
            };

            dispatch({
                type: 'update',
                newState: {
                    headers: _headers,
                    list: _list,
                    filters: {
                        StatusID: {
                            label: 'Status',
                            filters: filters.Status.map((filter) => {
                                return { id: filter.id, label: filter.description };
                            }),
                        },
                    },
                    recOffset: requestBody.RecOffset + 25,
                    hasMore: _list.length < response.totalCount,
                    currentCount: _list.length,
                    totalRecordCount: response.totalCount,
                    totalUnfilteredCount: requestBody?.FilterString === '' ? response.totalCount : requestObj.totalUnfilteredCount,
                },
            });
        } catch (e) {
            toast.error(t('General.AjaxError'));
        } finally {
            dispatch({ type: 'fetched' });
        }
    };

    const handleFilterString = (newVal, type) => {
        let newFilterString = '',
            newSearchString = '',
            newTotalString = '',
            newState = {};

        switch (type) {
            case 'search':
                if (newVal !== '') {
                    newSearchString = requestObj?.searchKeys.map((key) => `${key} LIKE '%${newVal}%'`).join(' OR ');

                    newTotalString =
                        requestObj?.filterObjectString !== ''
                            ? `${requestObj.filterObjectString} AND (${newSearchString})`
                            : newSearchString;
                } else {
                    newTotalString = requestObj?.filterObjectString;
                }

                newState.filterString = newTotalString;
                newState.searchString = newSearchString;
                newState.searchValue = newVal;
                break;
            case 'filter':
                let selectedFilterEntries = Object.entries(newVal);
                newFilterString = selectedFilterEntries
                    .map(([key, filters]) => {
                        return filters?.length > 0 ? `${key} IN (${filters.join(', ')})` : '';
                    })
                    .filter((str) => str !== '')
                    .join(' AND ');

                if (newFilterString !== '') {
                    newTotalString =
                        requestObj?.searchString !== '' ? `${newFilterString} AND (${requestObj?.searchString})` : newFilterString;
                    newTotalString += requestObj?.dateString !== '' ? ` AND ${requestObj.dateString}` : '';
                } else {
                    newTotalString = requestObj?.searchString !== '' ? requestObj?.searchString : '';
                }

                newState.filterString = newTotalString;
                newState.filterObjectString = newFilterString;
                newState.selectedFilters = newVal;
                break;
            case 'range':
                if (!!newVal?.To && !!newVal?.From) {
                    let toDate = typeof newVal.To?.toISOString === 'function' ? newVal.To.toISOString() : null;
                    let fromDate = typeof newVal.From?.toISOString === 'function' ? newVal.From.toISOString() : null;

                    fromDate = fromDate.substring(0, fromDate.indexOf('T')) + ' 00:00:00';
                    toDate = toDate.substring(0, toDate.indexOf('T')) + ' 23:59:59';

                    newFilterString = `LastUpdatedUTC BETWEEN '${fromDate}' AND '${toDate}'`;
                    newTotalString = `${requestObj.filterObjectString}${!!requestObj.filterObjectString ? ' AND ' : ''}${newFilterString}`;

                    newState.filterString = newTotalString;
                    newState.dateString = newFilterString;
                }
                break;
            default:
                return;
        }

        if (newState.filterString !== requestObj.filterString) {
            dispatch({ type: 'filter', newState });
            retrieveList(newState.filterString, requestObj.sort, 0);
        }
    };

    const handleSort = (key) => {
        let newState = {};

        if (key !== requestObj.sortKey) {
            newState.sortKey = key;
            newState.sortDir = 'Asc';

            newState.sort = `${key} Asc`;
        } else {
            newState.sortDir = requestObj?.sortDir === 'Asc' ? 'Desc' : 'Asc';

            newState.sort = `${key} ${newState.sortDir}`;
        }

        dispatch({ type: 'sort', newState });

        retrieveList(requestObj.filterString, newState.sort, 0);
    };

    const loadMore = async () => {
        await retrieveList();
    };

    const sendEmails = async (_recipients, _templateId) => {
        let successful = true;
        let failed = [];

        dispatch({ type: 'fetching' });

        var buckets = Math.ceil(_recipients.length / 25);
        var batch = 1;
        const toastId = toast.loading(`Sending ${batch} of ${buckets} email batches...`);
        for (let i = 0; i < _recipients.length; i += 25) {
            let currentBucket = _recipients.slice(i, i + 25);
            try {
                await API.post('ShipyardAPI', '/admin/notifications', {
                    body: {
                        recipients: currentBucket,
                        templateId: _templateId,
                    },
                });

                toast.update(toastId, {
                    render: `Sending ${batch++} of ${buckets} email batches...`,
                });
            } catch (e) {
                successful = false;
                failed.push(currentBucket);
            }
        }

        toast.dismiss(toastId);
        if (successful) {
            toast.success(`${_recipients.length} emails sent successfully!`);
        } else {
            toast.error(`${failed.length} emails possibly failed to send, check the logs and attempt the failures again.`);
        }

        dispatch({ type: 'fetched' });
        return successful;
    };

    const handlePageChange = (event, newPage) => {
        dispatch({
            type: 'update',
            newState: {
                page: newPage,
            },
        });
    };

    const handleRowsPerPageChange = (event) => {
        dispatch({
            type: 'update',
            newState: {
                page: 0,
                rowsPerPage: event.target.value,
            },
        });
    };

    return {
        ...requestObj,
        selected,
        setSelected,
        retrieveList,
        handleSort,
        handleFilterString,
        handlePageChange,
        handleRowsPerPageChange,
        loadMore,
        sendEmails,
    };
};

export default useNotificationDashboard;
